import React, { useMemo, useState, useEffect } from 'react';
import { useThrottleFn, useMemoizedFn } from 'ahooks';
import { DEFAULT_CALENDAR_COL_SPACING } from '../constants';

import type { ICalendarColCfgMapping } from '../types';

const getCardListElUISize = (
  cardListEl?: React.RefObject<HTMLElement>,
): {
  offsetWidth: number;
  scrollWidth: number;
} => {
  const { offsetWidth = 0, scrollWidth = 0 } = cardListEl?.current || {};
  return {
    ...cardListEl?.current,
    offsetWidth: offsetWidth || 0,
    scrollWidth: scrollWidth || 0,
  };
};

function useReact(props: {
  // 日历卡片外层容器（支持滚动那个dom）
  cardListEl?: React.RefObject<HTMLElement>;
  // 重新计算的依赖
  deps: React.DependencyList;
}) {
  const { cardListEl, deps } = props;

  const [rect, setRect] = useState({
    offsetWidth: 0,
    scrollWidth: 0,
  });

  const handleResize = () => {
    setTimeout(() => {
      const { offsetWidth, scrollWidth } = getCardListElUISize(cardListEl);
      setRect({
        offsetWidth: offsetWidth || 0,
        scrollWidth: scrollWidth || 0,
      });
    });
  };

  const { run: handleResizeRun } = useThrottleFn(handleResize, { wait: 50, leading: true, trailing: true });

  useEffect(() => {
    handleResize();
  }, [deps]);

  useEffect(() => {
    window.addEventListener('resize', handleResizeRun, false);

    return () => {
      window.removeEventListener('resize', handleResizeRun, false);
    };
  }, [deps]);

  return rect;
}

export default function useCalendarCardScrollable(props: {
  // 日历卡片外层容器（支持滚动那个dom）
  cardListEl?: React.RefObject<HTMLElement>;
  // 是否正在加载日历数据
  isLoading?: boolean;
  // 是否无日历数据
  isNoData?: boolean;
  // 日历数据
  calendarDataList?: CommonCalendarDataTypes;
  // 当前时间
  current?: string;
  // 列间距
  colSpacing?: number;
  // 列与列配置映射（键为时间，值为列配置）
  colCfgMapping?: ICalendarColCfgMapping;
  // 重新计算的依赖
  deps: React.DependencyList;
}) {
  const {
    cardListEl,
    isLoading = true,
    isNoData = true,
    calendarDataList,
    current,
    colSpacing,
    colCfgMapping,
    deps,
  } = props;

  const colSpacingTmp = colSpacing || DEFAULT_CALENDAR_COL_SPACING;

  // 日历卡片容器的scrollLeft
  const [scrollLeft, setScrollLeft] = useState(0);

  // 日历卡片外层容器（支持滚动那个dom）的Rect信息
  const rect = useReact({ cardListEl, deps });

  // 日历卡片容器是否水平方向溢出
  const isOverflowX = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    return rect.scrollWidth > rect.offsetWidth;
  }, [isLoading, isNoData, rect]);

  // 日历卡片外层容器（支持滚动那个dom）最大滚动距离
  const maxScrollLeft = useMemo<number>(() => {
    if (isLoading || isNoData) return Number.NaN;
    return rect.scrollWidth - rect.offsetWidth;
  }, [isLoading, isNoData, rect]);

  // 当前时间节点之前的时间节点对应的列的总宽度
  const prevColsWidth = useMemo<number>(() => {
    if (isLoading || isNoData) return Number.NaN;
    if (!Array.isArray(calendarDataList) || !calendarDataList.length) return Number.NaN;
    const currentCalendarData = calendarDataList.find((calendarDataItem) => calendarDataItem?.date === current);
    if (!currentCalendarData) return Number.NaN;
    let distance = 0;
    for (const calendarCardDataItem of calendarDataList) {
      const { date } = calendarCardDataItem || {};
      if (date === current) break;
      const colCfg = colCfgMapping?.[date];
      const colWidth = colCfg?.width || 0;
      distance += colWidth + colSpacingTmp;
    }
    return distance > 0 ? distance - colSpacingTmp : 0;
  }, [isLoading, isNoData, current, calendarDataList, colSpacingTmp, colCfgMapping]);

  // 当前时间节点对应的列 在滚动容器的可视范围内的最小scrollLeft、最大scrollLeft
  const { min, max } = useMemo<{
    min: number;
    max: number;
  }>(() => {
    if (isLoading || isNoData) return { min: Number.NaN, max: Number.NaN };
    if (!cardListEl?.current || !current || !colCfgMapping) return { min: Number.NaN, max: Number.NaN };
    if (!isOverflowX) return { min: Number.NaN, max: Number.NaN };
    if (Number.isNaN(prevColsWidth)) return { min: Number.NaN, max: Number.NaN };
    if (Number.isNaN(maxScrollLeft)) return { min: Number.NaN, max: Number.NaN };
    const currentColCfg = colCfgMapping?.[current];
    const currentCardWidth = currentColCfg?.cardWidth || 0;
    const prevColsDistance = prevColsWidth > 0 ? prevColsWidth + colSpacingTmp : 0;
    return {
      min: Math.max(prevColsWidth - rect.offsetWidth + colSpacingTmp + currentCardWidth, 0),
      max: Math.min(prevColsDistance, maxScrollLeft),
    };
  }, [isLoading, isNoData, current, isOverflowX, colCfgMapping, colSpacingTmp, prevColsWidth, rect, maxScrollLeft]);

  const updateScrollLeft = useMemoizedFn((scrollLeft?: number) => {
    if (Number.isNaN(maxScrollLeft)) return;
    if (typeof scrollLeft !== 'number' || Number.isNaN(scrollLeft)) return;
    setScrollLeft(Math.min(maxScrollLeft, scrollLeft));
  });

  const { run: updateScrollLeftRun } = useThrottleFn(updateScrollLeft, { wait: 50, leading: true, trailing: true });

  const handleScroll = useMemoizedFn((e: any) => {
    updateScrollLeftRun(e?.target?.scrollLeft);
  });

  // 是否自动定位滚动结束
  const [isAutoScrollFinished, setIsAutoScrollFinished] = useState(false);
  /**
   * 初始化时
   * 若当前日历中存在当前时间对应的日历，则把当前时间对应的日历滚动到最左侧（不一定能滚动到最左侧，受限制与日历卡片容器的最大可滚动数值）
   * 若不存在，则把日历数据中最开始的时间对应的日历滚动到最左侧（即日历卡片容器的scrollLeft值为0）
   */
  useEffect(() => {
    if (isLoading || isNoData) return;
    if (!isOverflowX) return;
    if (Number.isNaN(prevColsWidth)) return;
    if (Number.isNaN(maxScrollLeft)) return;
    const prevColsDistance = prevColsWidth > 0 ? prevColsWidth + colSpacingTmp : 0;
    setScrollLeft(Math.min(prevColsDistance, maxScrollLeft));
    if (cardListEl?.current) {
      cardListEl.current.scrollLeft = prevColsDistance;
      setIsAutoScrollFinished(true);
    }

    return () => {
      setIsAutoScrollFinished(false);
    };
  }, [isLoading, isNoData, isOverflowX, prevColsWidth, maxScrollLeft]);

  return {
    // 是否自动定位滚动结束
    isAutoScrollFinished,
    // 日历卡片容器是否水平方向溢出
    isOverflowX,
    // 日历卡片容器最小scrollLeft（当前时间对应的日历卡片在可视区域内）
    min,
    // 日历卡片容器最大scrollLeft（当前时间对应的日历卡片在可视区域内）
    max,
    // 日历卡片外层容器（支持滚动那个dom）最大滚动距离
    maxScrollLeft,
    // 当前时间对应的scrollLeft（即日历卡片容器的scrollLeft为该值时，当前时间则位于可视区域的最左侧）
    currentTimeScrollLeft: prevColsWidth,
    // 日历卡片容器的scrollLeft
    scrollLeft,
    // 日历卡片容器滚动的回调
    onScroll: handleScroll,
  };
}
